import { Fragment, JSX, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUndo } from 'react-icons/fa';
import { FaCheck, FaMinus, FaXmark } from 'react-icons/fa6';
import {
  MdEditSquare,
  MdOutlineCancel,
  MdOutlinePendingActions,
  MdOutlineTaskAlt,
  MdRemoveCircleOutline,
} from 'react-icons/md';
import { Button } from '../../components/button';
import { DateSelector } from '../../components/date-selector';
import { Form } from '../../components/form';
import { Plus } from '../../components/plus';
import { useAuthContext } from '../../contexts/auth.context';
import { Habit, isCountTask, isDayTask } from '../../dto/habit';
import { HabitFrequencyType, HabitTargetType, TargetDirection } from '../../dto/habit.enum';
import { Record } from '../../dto/record';
import { useHabit } from '../../hooks/habit.hook';
import { useLayout } from '../../hooks/layout.hook';
import { useNavigation } from '../../hooks/navigation.hook';
import { useRecord } from '../../hooks/record.hook';
import {
  daysAfter,
  daysBefore,
  getDay,
  getLocaleMonthDate,
  getWeekDay,
  getWeekNumber,
  isInCurrentMonth,
  isInCurrentWeek,
  isSameDay,
} from '../../util/util';

export default function HabitScreen(): JSX.Element {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const { user } = useAuthContext();

  const { habitsLoading, habits } = useHabit();
  const { recordsLoading } = useRecord();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedHabit, setSelectedHabit] = useState<Habit>();
  const [selectedRecord, setSelectedRecord] = useState<Record>();

  const selectedDay = useMemo(() => getDay(selectedDate), [selectedDate]);

  const hasHabits = habits.length > 0;
  const habitList = useMemo(() => {
    const activeHabits = habits.filter((h) => h.start <= selectedDay && (!h.end || h.end > selectedDay));
    const dailyHabits = activeHabits.filter((h) => !isCountTask(h));
    const otherHabits = activeHabits.filter((h1) => !dailyHabits.some((h2) => h2.id === h1.id));

    return [
      { title: 'Daily Tasks', items: dailyHabits },
      { title: 'Other Tasks', items: otherHabits },
    ];
  }, [habits, selectedDay]);

  const hasMultipleCategories = habitList.filter((i) => i.items.length > 0).length > 1;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const modalRef = useRef<HTMLDialogElement>(null);

  const openModal = useCallback((habit: Habit, record?: Record) => {
    setSelectedHabit(habit);
    setSelectedRecord(record);
    modalRef.current?.showModal();
  }, []);

  const closeModal = useCallback(() => {
    setSelectedHabit(undefined);
    setSelectedRecord(undefined);
    modalRef.current?.close();
  }, []);

  useLayout(
    () => ({
      activeMenu: 'habits',
      title: habits.length || habitsLoading || !user ? t('Habits') : t('Hello, {{name}}!', { name: user.name }),
      loading: habitsLoading || recordsLoading,
    }),
    [t, habitsLoading, recordsLoading, habits, user],
  );

  return (
    <>
      {hasHabits ? (
        <>
          <DateSelector dateCount={30} date={selectedDate} setDate={setSelectedDate} />

          {habitList
            .filter((c) => c.items.length)
            .map((c) => (
              <Fragment key={c.title}>
                {hasMultipleCategories ? (
                  <h2 className="text-xl text-center mt-5 mb-1">{t(c.title)}</h2>
                ) : (
                  <div className="mt-5" />
                )}
                <div className="flex flex-col gap-2">
                  {c.items.map((h) => (
                    <HabitItem key={h.id} habit={h} date={selectedDate} day={selectedDay} onOpen={openModal} />
                  ))}
                </div>
              </Fragment>
            ))}

          <RecordModal
            ref={modalRef}
            date={selectedDate}
            habit={selectedHabit}
            record={selectedRecord}
            onClose={closeModal}
          />
        </>
      ) : (
        <>
          <div className="text-center text-base-content/70">
            {t('Start your journey here, track your habits, and become the best version of yourself!')}
          </div>
        </>
      )}

      <Plus className="mt-6" onClick={() => navigate('/habits/add')} />
    </>
  );
}

// --- HABIT ITEM --- //

interface HabitItemProps {
  habit: Habit;
  date: Date;
  day: Date;
  onOpen: (habit: Habit, record?: Record) => void;
}

function HabitItem({ habit, date, day, onOpen }: HabitItemProps): JSX.Element {
  const { t } = useTranslation();
  const { recordsFor } = useRecord();
  const { navigate } = useNavigation();

  const {
    record,
    completedRecordCount,
    isEnded,
    isFuture,
    isBlocked,
    isCount,
    isToday,
    isEditableToday,
    isComplete,
    isNeutral,
    isFailed,
    isPending,
    isInCurrentPeriod,
  } = useMemo(() => {
    // get records
    const from = fromDate(habit);
    const to = toDate(habit);

    const records = recordsFor(habit, from, to);
    const record = records.findLast((r) => isSameDay(r.date, day));

    // general status
    const isEnded = Boolean(habit.end);
    const isFuture = date > new Date();
    const isBlocked = isEnded || isFuture;

    const isCount = isCountTask(habit);
    const isToday = isDayTask(habit, date);
    const isEditableToday = isToday && !isBlocked;

    // status icon
    const isComplete =
      record &&
      ((record.achievement >= record.target && habit.target.direction !== TargetDirection.REDUCTION) ||
        (record.achievement <= record.target && habit.target.direction === TargetDirection.REDUCTION) ||
        (habit.target.type === HabitTargetType.RATING && record.achievement > 7));
    const isNeutral = record && !isComplete && habit.target.type === HabitTargetType.RATING && record.achievement > 3;
    const isFailed = record && !isComplete;
    const isPending = !isFailed && !isComplete && !isCount;

    // count status
    const completedRecordCount = records.filter(
      (r) => r.achievement >= r.target || habit.target.type !== HabitTargetType.BOOLEAN,
    ).length;
    const isInCurrentPeriod =
      habit.frequency.type === HabitFrequencyType.WEEKLY ? isInCurrentWeek(date) : isInCurrentMonth(date);

    return {
      record,
      completedRecordCount,
      isEnded,
      isFuture,
      isBlocked,
      isCount,
      isToday,
      isEditableToday,
      isComplete,
      isNeutral,
      isFailed,
      isPending,
      isInCurrentPeriod,
    };
  }, [habit, date, day, recordsFor]);

  function fromDate(habit: Habit): Date {
    switch (habit.frequency.type) {
      case HabitFrequencyType.WEEKLY:
        return daysBefore(getWeekDay(day), day);

      case HabitFrequencyType.MONTHLY:
        return daysBefore(day.getDate() - 1, day);

      default:
        return day;
    }
  }

  function toDate(habit: Habit): Date {
    switch (habit.frequency.type) {
      case HabitFrequencyType.WEEKLY:
        return daysAfter(7 - getWeekDay(day), day);

      case HabitFrequencyType.MONTHLY: {
        const endOfMonth = new Date(day);
        endOfMonth.setMonth(endOfMonth.getMonth() + 1);
        endOfMonth.setDate(1);
        return endOfMonth;
      }

      default:
        return date;
    }
  }

  const timeString =
    habit.frequency.type === HabitFrequencyType.WEEKLY
      ? isInCurrentPeriod
        ? t('this week')
        : `${t('in week')} ${getWeekNumber(date)}`
      : isInCurrentPeriod
      ? t('this month')
      : `${t('in')} ${getLocaleMonthDate(date)}`;

  const btnStyle = isEditableToday
    ? 'btn-primary'
    : isEnded || (isFuture && !isToday)
    ? 'btn-neutral btn-disabled'
    : 'btn-neutral text-neutral-content/50';

  return (
    <div
      key={habit.id}
      className={`btn flex gap-2 items-center h-auto py-3 ${btnStyle}`}
      onClick={() =>
        isEditableToday ? onOpen(habit, record) : !isBlocked && navigate('/habits/edit', { state: habit })
      }
      onContextMenu={(e) => {
        e.preventDefault();
        navigate('/habits/edit', { state: habit });
      }}
    >
      <div className="text-left text-base font-bold flex-grow-1">{t(habit.title)}</div>

      {isToday && !isFuture && (
        <>
          {isCount && (
            <div
              className={`text-right ${
                completedRecordCount >= habit.frequency.count
                  ? 'text-success/70'
                  : isInCurrentPeriod
                  ? isEnded
                    ? 'text-warning/70'
                    : 'text-warning'
                  : 'text-error/70'
              }`}
            >
              {completedRecordCount} / {habit.frequency.count} {timeString}
            </div>
          )}

          {isComplete ? (
            <div className="text-success/70">
              <MdOutlineTaskAlt size={'1.5rem'} />
            </div>
          ) : isNeutral ? (
            <div className="text-secondary/70">
              <MdRemoveCircleOutline size={'1.5rem'} />
            </div>
          ) : isFailed ? (
            <div className="text-error/70">
              <MdOutlineCancel size={'1.5rem'} />
            </div>
          ) : (
            <div className={`text-warning ${!isPending && 'invisible'}`}>
              <MdOutlinePendingActions size={'1.5rem'} />
            </div>
          )}
        </>
      )}

      {!isToday && !isBlocked && (
        <div>
          <MdEditSquare size={'1.5rem'} />
        </div>
      )}
    </div>
  );
}

// --- RECORD MODAL --- //

interface RecordModalProps {
  ref: RefObject<HTMLDialogElement | null>;
  date: Date;
  habit?: Habit;
  record?: Record;
  onClose: () => void;
}

function RecordModal({ ref, date, habit, record, onClose }: RecordModalProps): JSX.Element {
  const { t } = useTranslation();
  const { addOrUpdateRecord, deleteRecord } = useRecord();
  const { navigate } = useNavigation();

  const [achievement, setAchievement] = useState<string>();
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    setAchievement((record?.achievement ?? (habit?.target.type === HabitTargetType.RATING ? '1' : '')).toString());
  }, [habit, record]);

  function onDelayedClose() {
    setTimeout(onClose, 100);
  }

  function onUpdate(habit: Habit, value?: string, isButtonSelect?: boolean) {
    if (isButtonSelect) setAchievement(value ?? '');

    const task = value ? addOrUpdateRecord(habit, date, +value) : record ? deleteRecord(record) : Promise.resolve();

    setIsUpdating(true);
    task.then(isButtonSelect ? onDelayedClose : onClose).finally(() => setIsUpdating(false));
  }

  function onEdit(habit: Habit) {
    navigate('/habits/edit', { state: habit });
  }

  return (
    <dialog ref={ref} className="modal">
      {habit && (
        <div className="modal-box">
          <div className="flex justify-between items-start relative">
            <h2 className="font-bold text-xl">{t(habit.title)}</h2>
            <button className="btn btn-square btn-ghost text-base-content/70 -mt-1.5" onClick={() => onEdit(habit)}>
              <MdEditSquare size={'1.5rem'} />
            </button>
          </div>

          <div className="my-2 font-normal text-base-content/70 whitespace-pre-wrap">
            {habit.notes && t(habit.notes)}
          </div>

          <div className="text-center">
            {habit.target.type === HabitTargetType.BOOLEAN ? (
              <>
                <div className="my-2">{t('Did you make it today?')}</div>
                <div className="join">
                  <button
                    className={`join-item btn btn-xl ${achievement === '1' && 'btn-success'}`}
                    onClick={() => onUpdate(habit, '1', true)}
                  >
                    <FaCheck size={'2rem'} />
                  </button>

                  <button
                    className={`join-item btn btn-xl ${!achievement && 'bg-secondary'}`}
                    onClick={() => onUpdate(habit, undefined, true)}
                  >
                    <FaMinus size={'2rem'} />
                  </button>

                  <button
                    className={`join-item btn btn-xl ${achievement === '-1' && 'btn-error'}`}
                    onClick={() => onUpdate(habit, '-1', true)}
                  >
                    <FaXmark size={'2rem'} />
                  </button>
                </div>
              </>
            ) : (
              <Form onSubmit={() => onUpdate(habit, achievement)}>
                <div className="my-2 relative">
                  {habit.target.type === HabitTargetType.RATING ? (
                    <>
                      <div className="text-left">{t('How did it go today?')}</div>
                      <input
                        type="range"
                        min={1}
                        max="10"
                        value={achievement}
                        className="range range-primary w-full"
                        step="1"
                        onChange={(e) => setAchievement(e.target.value ?? '')}
                      />
                      <div className="flex justify-between px-1 my-2 text-xs w-full">
                        {Array.from({ length: 10 }).map((_, index) => (
                          <div key={index} className="w-[16px] text-center">
                            {index + 1}
                          </div>
                        ))}
                      </div>
                      {!isUpdating && record && (
                        <Button
                          className="btn btn-square btn-ghost absolute text-base-content/70 -top-3 -right-1"
                          onClick={() => onUpdate(habit, undefined)}
                        >
                          <FaUndo />
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-left text-base-content/70">
                        {t('How many or how much did you do today?')}
                      </div>
                      <label className="input input-lg my-2">
                        <input
                          type="number"
                          className="w-full"
                          placeholder={`${habit.target.value}`}
                          value={achievement}
                          onChange={(e) => setAchievement(e.target.value)}
                        />
                        <span className="label">{t(`/ ${record?.target ?? habit.target.value}`)}</span>
                      </label>
                    </>
                  )}
                </div>

                <Button className="btn btn-primary btn-lg mt-2" type="submit" loading={isUpdating}>
                  {t('Confirm')}
                </Button>
              </Form>
            )}
          </div>
        </div>
      )}
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose}>Close</button>
      </form>
    </dialog>
  );
}
